import { IconHeartHandshake, type IconProps } from '@tabler/icons-react-native';
import { cloneElement, type ReactElement } from 'react';
import { type StyleProp, type ViewProps, type ViewStyle } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export type BadgeVariant = 'blue' | 'red' | 'tonal';
export type BadgeSize = 'small' | 'large';

interface BadgeBaseProps extends Pick<ViewProps, 'accessibilityLabel' | 'accessibilityLiveRegion' | 'testID'> {
  variant?: BadgeVariant;
  border?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** Omit accessibilityLabel for decorative dots/icons already described by their parent. */
export type BadgeProps = BadgeBaseProps &
  (
    | { type?: 'dot'; size?: never; label?: never; icon?: never }
    | { type: 'text'; size?: BadgeSize; label: string | number; icon?: never }
    | { type: 'icon'; size?: never; label?: never; icon?: ReactElement<IconProps> }
  );

const BACKGROUND_BY_VARIANT = {
  blue: 'surface.info.medium',
  red: 'surface.error.medium',
  tonal: 'surface.brand.default',
} as const;

function BadgeOutline() {
  const theme = useTheme();
  const borderWidth = theme.borderWidths['border-width.subtle'];
  // Figma uses an outside stroke, which must not consume the small text's line box.
  return (
    <Box
      position="absolute"
      top={-borderWidth}
      right={-borderWidth}
      bottom={-borderWidth}
      left={-borderWidth}
      borderWidth={borderWidth}
      borderColor="border.inverse.primary"
      borderRadius="radius.full"
      style={{ pointerEvents: 'none' }}
    />
  );
}

function BadgeContent({
  type,
  variant = 'blue',
  size,
  label,
  icon,
  border,
}: Pick<BadgeProps, 'type' | 'variant' | 'size' | 'label' | 'icon' | 'border'>) {
  const theme = useTheme();
  const outline = border ? <BadgeOutline /> : null;
  if (type === 'dot') {
    const diameter = Number(theme.tokens.sizing['sizing.general.x2']);
    return (
      <Box
        width={diameter}
        height={diameter}
        borderRadius="radius.full"
        backgroundColor={BACKGROUND_BY_VARIANT[variant]}
      >
        {outline}
      </Box>
    );
  }

  return (
    <>
      {type === 'icon' ? (
        cloneElement(icon ?? <IconHeartHandshake />, {
          size: Number(theme.tokens.sizing['sizing.icon.s']),
          color: theme.colors['icon.inverse.primary'],
          strokeWidth: ICON_STROKE_WIDTH,
          accessible: false,
        })
      ) : (
        <Text
          variant={size === 'large' ? 'captionLargeMedium' : 'captionBaseMedium'}
          color="text.inverse.primary"
          numberOfLines={1}
          accessible={false}
          style={{ includeFontPadding: false }}
        >
          {label}
        </Text>
      )}
      {outline}
    </>
  );
}

/** Figma Badge: dot, small/large text, and large icon; badges are non-interactive. */
export function Badge({
  type = 'dot',
  variant = 'blue',
  size = 'small',
  label = '1',
  icon,
  border = false,
  accessibilityLabel,
  accessibilityLiveRegion,
  testID,
  style,
}: BadgeProps) {
  const theme = useTheme();
  const isDot = type === 'dot';
  const isLarge = type === 'icon' || (type === 'text' && size === 'large');
  const diameter = Number(theme.tokens.sizing[isLarge ? 'sizing.general.x5' : 'sizing.general.x3']);
  const accessibleLabel = accessibilityLabel ?? (type === 'text' ? String(label) : undefined);

  return (
    <Box
      accessible={!!accessibleLabel}
      accessibilityRole={accessibleLabel ? 'text' : undefined}
      accessibilityLabel={accessibleLabel}
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityElementsHidden={!accessibleLabel}
      importantForAccessibility={accessibleLabel ? 'yes' : 'no-hide-descendants'}
      testID={testID}
      alignSelf="center"
      flexShrink={0}
      alignItems="center"
      justifyContent="center"
      minWidth={diameter}
      minHeight={diameter}
      width={isDot ? diameter : undefined}
      height={isDot ? diameter : undefined}
      paddingHorizontal={isDot ? 'spacing.general.0' : isLarge ? 'spacing.general.x1' : 'spacing.general.x0,5'}
      backgroundColor={isDot ? undefined : BACKGROUND_BY_VARIANT[variant]}
      borderRadius="radius.full"
      style={style}
    >
      <BadgeContent type={type} variant={variant} size={size} label={label} icon={icon} border={border} />
    </Box>
  );
}
